import React, { useState, useRef, useEffect } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { AppNotification } from '../types';
import './NotificationBell.css';

const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<AppNotification | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications
  } = useNotifications();

  // Navigation function for different notification types
  const getNavigationPath = (notification: AppNotification): string => {
    switch (notification.notification_type) {
      case 'new_payment':
        // New payment awaiting approval - redirect to approvals page
        if (notification.pending_sale_id) {
          return `/approvals?highlight=${notification.pending_sale_id}`;
        }
        return '/approvals';

      case 'payment_approved':
        // Payment approved - redirect to pending processing page
        if (notification.pending_sale_id) {
          return `/pending-processing?highlight=${notification.pending_sale_id}`;
        }
        return '/pending-processing';

      case 'payment_rejected':
        // Payment rejected - redirect to the rejected transactions view (routable
        // even though it's hidden from the sidebar). A rejected sale is never
        // charged, so it won't appear in the paid-only "My Pending Processing".
        if (notification.pending_sale_id) {
          return `/rejected-transactions?highlight=${notification.pending_sale_id}`;
        }
        return '/rejected-transactions';

      case 'processing_assigned':
        // Transaction assigned to processor - redirect to assigned processing page
        if (notification.sale_id) {
          return `/my-assigned-processing?highlight=${notification.sale_id}`;
        } else if (notification.transaction_id) {
          return `/my-assigned-processing?highlight=${notification.transaction_id}`;
        }
        return '/my-assigned-processing';

      case 'processing_completed':
        // Processing completed - redirect to transaction history
        if (notification.sale_id) {
          return `/transactions?highlight=${notification.sale_id}`;
        }
        return '/transactions';

      case 'comment_mention':
        // Mentioned in comment - redirect to transaction history
        if (notification.sale_id) {
          return `/transactions?highlight=${notification.sale_id}#comment-${notification.comment_id}`;
        }
        return '/transactions';

      case 'payment_failed':
        // Payment failed - redirect to failed transactions page
        if (notification.pending_sale_id) {
          return `/my-failed-transactions?highlight=${notification.pending_sale_id}`;
        }
        return '/my-failed-transactions';

      case 'chargeback_initiated':
        // Chargeback initiated - redirect to chargebacks page
        if (notification.transaction_id) {
          return `/chargebacks?highlight=${notification.transaction_id}`;
        }
        return '/chargebacks';

      default:
        // Fallback: try to use action_url or redirect to dashboard
        if (notification.action_url) {
          return notification.action_url;
        }
        return '/dashboard';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedNotification(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Get the navigation path based on notification type and context
    const navigationPath = getNavigationPath(notification);

    // Navigate to the appropriate page
    navigate(navigationPath);
    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    refreshNotifications();
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  const getPriorityClass = (priority: string): string => {
    switch (priority) {
      case 'high': return 'notification-priority-high';
      case 'medium': return 'notification-priority-medium';
      case 'low': return 'notification-priority-low';
      default: return 'notification-priority-medium';
    }
  };

  const getNotificationIcon = (type: string): string => {
    switch (type) {
      case 'new_payment': return '💳';
      case 'payment_approved': return '✅';
      case 'payment_rejected': return '❌';
      case 'processing_assigned': return '👤';
      case 'comment_mention': return '💬';
      case 'payment_failed': return '⚠️';
      default: return '🔔';
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className="notification-bell-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        {unreadCount > 0 ? (
          <BellSolidIcon className="notification-bell-icon notification-bell-icon-active" />
        ) : (
          <BellIcon className="notification-bell-icon notification-bell-icon-inactive" />
        )}

        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllRead}
                disabled={loading}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <div className="notification-empty-icon">🔔</div>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${
                    !notification.is_read ? 'notification-unread' : ''
                  } ${getPriorityClass(notification.priority)}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.notification_type)}
                  </div>

                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">{notification.message}</div>

                    <div className="notification-meta">
                      {notification.sender_name && (
                        <span className="notification-sender">from {notification.sender_name}</span>
                      )}
                      <span className="notification-time">
                        {formatTimeAgo(notification.created_at)}
                      </span>
                    </div>
                  </div>

                  {!notification.is_read && (
                    <div className="notification-unread-dot"></div>
                  )}
                </div>
              ))
            )}
          </div>

          {notifications.length > 10 && (
            <div className="notification-footer">
              <button className="view-all-btn">
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <div className="notification-modal-overlay" onClick={() => setSelectedNotification(null)}>
          <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notification-modal-header">
              <h3>{selectedNotification.title}</h3>
              <button
                className="modal-close-btn"
                onClick={() => setSelectedNotification(null)}
              >
                ×
              </button>
            </div>

            <div className="notification-modal-content">
              <p>{selectedNotification.message}</p>

              <div className="notification-modal-meta">
                <div>
                  <strong>Type:</strong> {selectedNotification.notification_type.replace('_', ' ')}
                </div>
                <div>
                  <strong>Priority:</strong> {selectedNotification.priority}
                </div>
                <div>
                  <strong>Created:</strong> {new Date(selectedNotification.created_at).toLocaleString()}
                </div>
                {selectedNotification.sender_name && (
                  <div>
                    <strong>From:</strong> {selectedNotification.sender_name}
                  </div>
                )}
              </div>
            </div>

            <div className="notification-modal-actions">
              <button
                className="btn-primary"
                onClick={() => {
                  const navigationPath = getNavigationPath(selectedNotification);
                  navigate(navigationPath);
                  setSelectedNotification(null);
                  setIsOpen(false);
                }}
              >
                Go to Item
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  deleteNotification(selectedNotification.id);
                  setSelectedNotification(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;